/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { xGAUpdate } from "@monsma/App/Handlers/GoogleAnalytics/GA/xGA";
import { xGaState } from "@monsma/App/Store/Reducers";
import { getDataLayer } from "@monsma/App/Utilities/DataLayer";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState & xGaState) => {
    let PDPProduct: any = null;
    let PLPProducts: any = [];

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);

    if (currentPage === "ProductDetailsPage") {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        const productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;

        if (productInfo?.pricing) {
            PDPProduct = { pricing: productInfo?.pricing, ...productState.value };
        }
    }

    if (currentPage === "ProductListPage") {
        const {
            pages: {
                productList: { parameter },
            },
        } = state;
        const productsDataView = getProductsDataView(state, parameter);

        if (productsDataView && productsDataView.value) {
            PLPProducts = [];

            productsDataView.value.forEach(product => {
                const productInfo = state.pages.productList.productInfosByProductId[product.id];

                if (productInfo?.pricing) {
                    PLPProducts.push({ ...product, pricing: productInfo?.pricing });
                }
            });
        }
    }

    return {
        currentPage,
        PDPProduct,
        PLPProducts,
        xGAState: state.xGA,
        currencyCode: state.context?.session?.currency?.currencyCode,
    };
};

const mapDispatchToProps = {
    xGAUpdate,
};

const enum fields {
    checkoutFlowEvent = "checkoutFlowEvent",
    checkoutFlowEventName = "checkoutFlowEventName",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    cartToBuyRateEvent = "cartToBuyRateEvent",
    cartToBuyRateEventName = "cartToBuyRateEventName",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    productBrand = "productBrand",
    productCategory = "productCategory",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkoutFlowEvent]: boolean;
        [fields.checkoutFlowEventName]: string;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.cartToBuyRateEvent]: boolean;
        [fields.cartToBuyRateEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.productBrand]: boolean;
        [fields.productCategory]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const GoogleAnalyticsWidget: FC<Props> = ({
    xGAUpdate,
    fields,
    currentPage,
    PDPProduct,
    PLPProducts,
    xGAState,
    currencyCode,
}) => {
    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);

    useEffect(() => {
        xGAUpdate({
            checkoutFlowEvent: fields["checkoutFlowEvent"] || false,
            checkoutFlowEventName: fields["checkoutFlowEventName"] || "checkout",
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "addToCart",
            cartToBuyRateEvent: fields["cartToBuyRateEvent"] || false,
            cartToBuyRateEventName: fields["cartToBuyRateEventName"] || "purchase",
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "removeFromCart",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "productDetails",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "productImpression",
            productBrand: fields["productBrand"] || false,
            productCategory: fields["productCategory"] || false,
        });
    }, [fields]);

    // PLP event handler
    useEffect(() => {
        const isPDP = currentPage === "ProductDetailsPage";
        const isPLP = currentPage === "ProductListPage";
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPDP && xGAState.pdpViewEvent && PDPProduct && !PDPInfoLoaded) {
                setPDPInfoLoaded(true);

                const data = {
                    event: xGAState.pdpViewEventName || "productDetails",
                    ecommerce: {
                        detail: {
                            actionField: { list: "Product Detail Page" },
                            products: [
                                {
                                    name: PDPProduct?.productTitle,
                                    id: PDPProduct?.productNumber,
                                    price: PDPProduct.pricing?.unitNetPrice,
                                },
                            ],
                        },
                    },
                } as any;

                if (xGAState["productBrand"]) {
                    data["brand"] = PDPProduct.brand?.name || "";
                }

                if (xGAState["productCategory"]) {
                    data["category"] = PDPProduct.properties["category"] || "";
                }

                dataLayer.push(data);
            }

            if (isPLP && xGAState.productImpressionEvent && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);

                const data = {
                    event: xGAState.productImpressionEventName || "productImpression",
                    ecommerce: {
                        currencyCode: currencyCode || "USD",
                        impressions: [] as any,
                    },
                };

                PLPProducts.forEach((product: any, idx: number) => {
                    const temp: any = {
                        name: product?.productTitle,
                        id: product?.productNumber,
                        price: product.pricing?.unitNetPrice,
                        position: idx,
                    };

                    if (xGAState["productBrand"]) {
                        temp["brand"] = product.brand?.name || "";
                    }

                    if (xGAState["productCategory"]) {
                        temp["category"] = product.properties["category"] || "";
                    }

                    data.ecommerce.impressions.push(temp);
                });

                dataLayer.push(data);
            }
        }
    }, [PLPProducts, PDPProduct, currentPage, xGAState]);

    // PLP event handler
    useEffect(() => {
        setPDPInfoLoaded(false);
        setPLPInfoLoaded(false);
    }, [currentPage]);

    return null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GoogleAnalyticsWidget),
    definition: {
        displayName: "Google Analytics Management",
        group: "GoogleAnalytics" as any,
        fieldDefinitions: [
            {
                name: fields.checkoutFlowEvent,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.checkoutFlowEventName,
                displayName: "Checkout Flow Event Name",
                editorTemplate: "TextField",
                defaultValue: "checkout",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart Event Name",
                editorTemplate: "TextField",
                defaultValue: "addToCart",
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.cartToBuyRateEvent,
                displayName: "Cart To Buy Rate (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.cartToBuyRateEventName,
                displayName: "Cart To Buy Rate Event Name",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove From Cart Line (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove From Cart Line Event Name",
                editorTemplate: "TextField",
                defaultValue: "removeFromCart",
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View Event Name",
                editorTemplate: "TextField",
                defaultValue: "productDetails",
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression Event Name",
                editorTemplate: "TextField",
                defaultValue: "productImpression",
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.productBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.productCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
